import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { get, post, patch, ApiError } from '../../lib/api';

const ROLES = ['cashier', 'supervisor', 'store_manager', 'area_manager', 'admin'];
const STORES = ['BH21101', 'BH21103', 'BH21102'];

/**
 * Staff, PINs and scopes.
 *
 * PINs are Argon2id-hashed server-side and never retrievable — an admin can only
 * RESET a PIN, never read one. Scopes can be granted individually on top of the
 * role preset, so you are never boxed in by the role list.
 */
export default function Staff() {
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [creating, setCreating] = useState(false);
  const [scopeEdit, setScopeEdit] = useState<any>(null);
  const [form, setForm] = useState({
    empCode: '', nameEn: '', nameAr: '', pin: '', role: 'cashier',
    storeCodes: ['BH21101'] as string[], commissionPctBp: 0,
  });

  const staff = useQuery({ queryKey: ['staff'], queryFn: () => get<any[]>('/api/v1/admin/staff') });
  const cat = useQuery({ queryKey: ['scopes'], queryFn: () => get<any>('/api/v1/admin/scopes') });

  const create = useMutation({
    mutationFn: (b: any) => post('/api/v1/admin/staff', b),
    onSuccess: () => {
      setMsg(`Created ${form.nameEn}. PIN is hashed and cannot be viewed again.`);
      setError(''); setCreating(false);
      setForm({ empCode: '', nameEn: '', nameAr: '', pin: '', role: 'cashier', storeCodes: ['BH21101'], commissionPctBp: 0 });
      void qc.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create staff member'),
  });

  const update = useMutation({
    mutationFn: (v: { id: string; body: any }) => patch(`/api/v1/admin/staff/${v.id}`, v.body),
    onSuccess: () => { setError(''); setScopeEdit(null); void qc.invalidateQueries({ queryKey: ['staff'] }); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Update failed'),
  });

  async function resetPin(u: any) {
    const pin = prompt(`New PIN for ${u.nameEn} (4-8 digits, no repeated or sequential runs):`);
    if (!pin) return;
    update.mutate({ id: u.id, body: { pin } });
    setMsg(`PIN reset for ${u.nameEn}`);
  }

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Staff, PINs &amp; permissions</h1>
          <p className="text-sm text-gray-400 mt-1">
            PINs are hashed with Argon2id and can never be read back — only reset.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Cancel' : 'Add staff member'}
        </button>
      </div>

      {msg && <div className="text-sm text-emerald-300 bg-emerald-950/40 border border-emerald-900 rounded-lg px-4 py-2">{msg}</div>}
      {error && <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-4 py-2">{error}</div>}

      {creating && (
        <div className="card p-5 grid md:grid-cols-3 gap-4">
          <div>
            <label className="label">Employee code</label>
            <input className="input w-full" value={form.empCode}
              onChange={(e) => setForm({ ...form, empCode: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <label className="label">Name (English)</label>
            <input className="input w-full" value={form.nameEn}
              onChange={(e) => setForm({ ...form, nameEn: e.target.value })} />
          </div>
          <div>
            <label className="label">Name (Arabic)</label>
            <input className="input w-full" dir="rtl" value={form.nameAr}
              onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
          </div>
          <div>
            <label className="label">PIN (4-8 digits)</label>
            <input className="input w-full money" value={form.pin} maxLength={8}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })} />
          </div>
          <div>
            <label className="label">Role</label>
            <select className="input w-full" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
            <div className="text-[11px] text-gray-500 mt-1">
              grants {cat.data?.presets?.[form.role]?.length ?? 0} scopes ·
              discount limit {cat.data?.discountLimits?.[form.role] ?? 0}%
            </div>
          </div>
          <div>
            <label className="label">Commission %</label>
            <input type="number" step="0.01" className="input w-full money"
              value={form.commissionPctBp / 100}
              onChange={(e) => setForm({ ...form, commissionPctBp: Math.round(Number(e.target.value) * 100) })} />
          </div>
          <div className="md:col-span-3">
            <label className="label">Stores</label>
            <div className="flex gap-2">
              {STORES.map((c) => (
                <button key={c}
                  className={`badge border px-3 py-1.5 ${
                    form.storeCodes.includes(c)
                      ? 'bg-brand text-white border-brand' : 'bg-gray-800 text-gray-300 border-gray-700'
                  }`}
                  onClick={() => setForm({
                    ...form,
                    storeCodes: form.storeCodes.includes(c)
                      ? form.storeCodes.filter((x) => x !== c) : [...form.storeCodes, c],
                  })}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <button className="btn-primary" disabled={create.isPending || !form.empCode || !form.nameEn || form.pin.length < 4}
              onClick={() => create.mutate(form)}>
              {create.isPending ? 'Creating…' : 'Create staff member'}
            </button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr>
            <th className="th">Code</th><th className="th">Name</th><th className="th">Role</th>
            <th className="th">Stores</th><th className="th">Discount limit</th>
            <th className="th">Commission</th><th className="th">Scopes</th><th className="th">Status</th><th className="th"></th>
          </tr></thead>
          <tbody>
            {staff.data?.map((u) => (
              <tr key={u.id}>
                <td className="td font-mono text-xs">{u.empCode}</td>
                <td className="td">
                  <div>{u.nameEn}</div>
                  {u.nameAr && <div className="text-[11px] text-gray-500" dir="rtl">{u.nameAr}</div>}
                </td>
                <td className="td">
                  <select className="input py-1 text-xs" value={u.role}
                    onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value } })}>
                    {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select>
                </td>
                <td className="td text-xs">{(u.storeCodes ?? []).join(', ')}</td>
                <td className="td">
                  <input type="number" className="input py-1 w-16 money text-xs" defaultValue={u.discountLimitPct}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v !== u.discountLimitPct) update.mutate({ id: u.id, body: { discountLimitPct: v } });
                    }} />
                </td>
                <td className="td money text-xs">{(u.commissionPct / 100).toFixed(2)}%</td>
                <td className="td">
                  <button className="text-xs text-brand hover:underline" onClick={() => setScopeEdit(u)}>
                    {(u.scopes ?? []).length} extra
                  </button>
                </td>
                <td className="td">
                  <span className={`badge ${u.active ? 'bg-emerald-900/50 text-emerald-300' : 'bg-gray-800 text-gray-500'}`}>
                    {u.active ? 'active' : 'inactive'}
                  </span>
                </td>
                <td className="td whitespace-nowrap">
                  <button className="text-xs text-gray-400 hover:text-brand mr-2" onClick={() => void resetPin(u)}>
                    Reset PIN
                  </button>
                  <button className="text-xs text-gray-400 hover:text-red-400"
                    onClick={() => update.mutate({ id: u.id, body: { active: !u.active } })}>
                    {u.active ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {scopeEdit && cat.data && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-40" onClick={() => setScopeEdit(null)}>
          <div className="card w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-800">
              <div className="font-medium">Extra permissions — {scopeEdit.nameEn}</div>
              <div className="text-[11px] text-gray-500">
                Role "{scopeEdit.role.replace('_', ' ')}" already grants its preset. Ticks here are ADDITIONAL grants.
              </div>
            </div>
            <div className="p-5 overflow-y-auto grid sm:grid-cols-2 gap-1.5">
              {cat.data.scopes.map((sc: string) => {
                const inPreset = (cat.data.presets[scopeEdit.role] ?? []).includes(sc);
                const granted = (scopeEdit.scopes ?? []).includes(sc);
                return (
                  <label key={sc} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${
                    inPreset ? 'opacity-50' : 'hover:bg-gray-800'
                  }`}>
                    <input type="checkbox" disabled={inPreset} checked={inPreset || granted}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(scopeEdit.scopes ?? []), sc]
                          : (scopeEdit.scopes ?? []).filter((x: string) => x !== sc);
                        setScopeEdit({ ...scopeEdit, scopes: next });
                      }} />
                    <span className="font-mono">{sc}</span>
                    {inPreset && <span className="text-gray-600">(from role)</span>}
                  </label>
                );
              })}
            </div>
            <div className="px-5 py-3 border-t border-gray-800 flex gap-2 justify-end">
              <button className="btn-ghost" onClick={() => setScopeEdit(null)}>Cancel</button>
              <button className="btn-primary"
                onClick={() => update.mutate({ id: scopeEdit.id, body: { scopes: scopeEdit.scopes ?? [] } })}>
                Save permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
